import { createAiSsoToken } from "./authService";

// Auth for every call to the Python Process Optimization agent (AI SSO).
// 1. No agent session yet → get a single-use SSO token from the CMS (login cookie).
// 2. Call the agent with Authorization: Bearer <sso_token>; it answers with an
//    X-AI-Session handle ("ais_…", ~28 min) that we keep IN MEMORY only.
// 3. Every later call (including job polling) sends Bearer <aiSession>, so we don't
//    spend an SSO token per call (the CMS issues at most 10 per minute per user).

export const SESSION_EXPIRED_EVENT = "app:session-expired";
export const AI_FORBIDDEN_MESSAGE = "You don't have access to this process.";

let aiSession = null; // never written to localStorage/sessionStorage
let starting = null; // in-flight request that is exchanging a fresh SSO token

export const clearAiSession = () => {
  aiSession = null;
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const fail = (message, status) => Object.assign(new Error(message), { status });

// Not logged in / session over (there is no refresh token) → App logs out to /login.
const endSession = () => {
  aiSession = null;
  window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
  return fail("Your session has expired. Please log in again.", 401);
};

async function getSsoToken(retried = false) {
  try {
    return (await createAiSsoToken()).token;
  } catch (e) {
    if (e.status === 401) throw endSession();
    if (e.status === 429 && !retried) {
      await sleep(5000);
      return getSsoToken(true);
    }
    throw fail("AI service unavailable", e.status);
  }
}

async function send(url, options, credential) {
  const res = await fetch(url, {
    ...options,
    credentials: "omit", // the agent authenticates by the Bearer header only
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
      Authorization: `Bearer ${credential}`,
    },
  });
  const handle = res.headers.get("X-AI-Session");
  if (handle) aiSession = handle;
  return [res, credential];
}

// Use the agent session when we have one. Otherwise exchange a fresh SSO token —
// and make parallel callers wait for that first exchange, so they reuse the
// session it returns instead of each spending an SSO token.
async function authorizedSend(url, options) {
  while (!aiSession && starting) await starting.catch(() => {});
  if (aiSession) return send(url, options, aiSession);
  starting = getSsoToken().then((token) => send(url, options, token));
  try {
    return await starting;
  } finally {
    starting = null;
  }
}

// fetch() for the agent. Handles auth and the agent's documented error statuses;
// any other response is returned as-is for the calling service to parse.
export async function aiFetch(url, options = {}) {
  let [res, used] = await authorizedSend(url, options);

  if (res.status === 401) {
    // Session expired or rejected: drop it, get a fresh SSO token, retry once.
    if (aiSession === used) aiSession = null;
    [res] = await authorizedSend(url, options);
    if (res.status === 401) throw endSession();
  }
  if (res.status === 429) {
    await sleep(3000);
    [res] = await authorizedSend(url, options);
  }
  if (res.status === 403) throw fail(AI_FORBIDDEN_MESSAGE, 403);
  if (res.status === 404 && /\/jobs\//.test(url)) {
    throw fail(
      "This job is no longer available (it expired or belongs to another user). Please run the action again.",
      404,
    );
  }
  if (res.status === 503) {
    throw fail("AI service temporarily unavailable, try again.", 503);
  }
  return res;
}
